/**
 * LISTEN_REPEAT speaking exercise: the candidate reads a scenario, hears a
 * sentence and repeats it aloud. The sentence text (`text`) is never shown;
 * it is the expected answer the pronunciation assessment listens for.
 *
 * - SINGLE (default): one sentence per exercise, `audioFileName` + `text`,
 *   recorded and scored exactly like READ.
 * - SERIES: `sentences[]` of { audioFileName, text } in one exercise. Each
 *   sentence is recorded separately (AS_{instance}_{exercise}_{n}.ogg) and the
 *   exercise is submitted once, after the last sentence.
 */
export const LISTEN_REPEAT = "LISTEN_REPEAT";

export function isListenRepeatSeries(exerciseType, data) {
  return exerciseType === LISTEN_REPEAT && data?.mode === "SERIES";
}

export function listenRepeatSentences(data) {
  if (data?.mode === "SERIES") {
    return Array.isArray(data?.sentences) ? data.sentences.filter(Boolean) : [];
  }
  return [{ audioFileName: data?.audioFileName, text: data?.text }];
}

/** The pronunciation-assessment reference text for the current recording. */
export function referenceText(exerciseType, data, sentenceIndex = 0) {
  if (isListenRepeatSeries(exerciseType, data)) {
    const text = listenRepeatSentences(data)[sentenceIndex]?.text;
    return typeof text === "string" ? text : "";
  }
  return data?.text ? data.text : "";
}

const RECORDING_SUFFIX = ".ogg";

/** Recording of sentence `index` (0-based) of a series: AS_1_2_1.ogg, ... */
export function seriesRecordingFileName(baseFileName, index) {
  return baseFileName.endsWith(RECORDING_SUFFIX)
    ? `${baseFileName.slice(0, -RECORDING_SUFFIX.length)}_${index + 1}${RECORDING_SUFFIX}`
    : `${baseFileName}_${index + 1}`;
}

const SCORE_FIELDS = ["pronScore", "accuracyScore", "fluencyScore", "completenessScore", "confidence"];

function toNumber(value) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

function mean(values) {
  const present = values.filter((v) => v !== undefined);
  if (present.length === 0) return undefined;
  return present.reduce((a, b) => a + b, 0) / present.length;
}

/**
 * Combines the recorded sentences of a series into one result. `takes[i]` is
 * { aggregate, segments, recordingFileName, recordingFileUrl } of sentence i;
 * a sentence without recognised speech has no segments and counts as
 * NO_AUDIO. Score fields are averaged over the sentences that produced
 * speech; word count and duration are summed.
 */
export function combineSeriesTakes(takes) {
  const sentences = takes.map((take, index) => {
    const segments = Array.isArray(take?.segments) ? take.segments : [];
    const spoken = segments.length > 0;
    const aggregate = spoken ? take?.aggregate || {} : {};
    const sentence = {
      index,
      status: spoken ? "OK" : "NO_AUDIO",
      recordingFileName: take?.recordingFileName,
      recordingFileUrl: take?.recordingFileUrl,
      transcript: segments.map((s) => s?.DisplayText).filter(Boolean).join(" "),
      noAudioReason: spoken ? undefined : take?.noAudioReason,
      wordCount: spoken ? Number(aggregate.wordCount) || 0 : 0,
      duration: spoken ? toNumber(aggregate.duration) : undefined,
      wordPerMinuteWordsOnly: spoken ? toNumber(aggregate.wordPerMinuteWordsOnly) : undefined,
    };
    for (const field of SCORE_FIELDS) {
      sentence[field] = spoken ? toNumber(aggregate[field]) : undefined;
    }
    return sentence;
  });

  const spokenSentences = sentences.filter((s) => s.status === "OK");
  const data = {};
  for (const field of SCORE_FIELDS) {
    const value = mean(spokenSentences.map((s) => s[field]));
    data[field] = value === undefined ? undefined : value.toFixed(field === "confidence" ? 2 : 0);
  }
  data.wordCount = sentences.reduce((sum, s) => sum + s.wordCount, 0);
  const duration = spokenSentences.reduce((sum, s) => sum + (s.duration || 0), 0);
  data.duration = duration.toFixed(2);
  const wpm = mean(spokenSentences.map((s) => s.wordPerMinuteWordsOnly));
  data.wordPerMinuteWordsOnly = wpm === undefined ? undefined : wpm.toFixed(0);
  data.sentences = sentences;

  const rawData = takes.flatMap((take, index) =>
    (Array.isArray(take?.segments) ? take.segments : []).map((segment) => ({
      ...segment,
      sentenceIndex: index,
    })),
  );

  return { data, rawData };
}

const LEGACY_RECORDING_BASE_URL =
  "https://storage.googleapis.com/sensay-assessment/recordings/";

/** Playable URL of a recording: its stored URL, else the legacy GCS path. */
export function recordingUrl(recordingFileUrl, recordingFileName) {
  if (recordingFileUrl) return recordingFileUrl;
  return recordingFileName ? LEGACY_RECORDING_BASE_URL + recordingFileName : undefined;
}

/**
 * Report players of a speaking result row: one per recorded sentence of a
 * LISTEN_REPEAT series (sentences without audio are left out), otherwise the
 * row's single recording. Returns [{ index, url }].
 */
export function reportRecordings(item) {
  let answer;
  try {
    answer = item?.answerData ? JSON.parse(item.answerData) : undefined;
  } catch (_) {
    answer = undefined;
  }
  if (Array.isArray(answer?.sentences)) {
    return answer.sentences
      .filter((s) => s && s.status !== "NO_AUDIO")
      .map((s, i) => ({
        index: Number.isInteger(s.index) ? s.index : i,
        url: recordingUrl(s.recordingFileUrl, s.recordingFileName),
      }))
      .filter((r) => r.url);
  }
  const url = recordingUrl(item?.recordingFileUrl, item?.recordingFileName);
  return url ? [{ index: 0, url }] : [];
}
